import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMatches, fetchStats } from "../lib/api";
import { Search, CheckCircle2, XCircle, MinusCircle, Calendar, ChevronDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const statusStyles = {
  WIN:  { pill: "bg-emerald-500 text-white",         icon: CheckCircle2 },
  LOSS: { pill: "bg-[#ff5a5a] text-white",           icon: XCircle },
  DRAW: { pill: "bg-slate-400 text-white",           icon: MinusCircle },
};

const monthName = (m) =>
  ["January","February","March","April","May","June",
   "July","August","September","October","November","December"][m];

const formatDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const groupByMonth = (list) => {
  const groups = {};
  for (const m of list) {
    if (!m.date) continue;
    const [y, mo] = m.date.split("-");
    const key = `${y}-${mo}`;
    const label = `${monthName(parseInt(mo, 10) - 1)} ${y}`;
    if (!groups[key]) groups[key] = { key, label, y, mo, items: [] };
    groups[key].items.push(m);
  }
  return Object.values(groups).sort((a, b) => (a.key < b.key ? 1 : -1));
};

const monthTotals = (items) => {
  const wins = items.filter((r) => r.status === "WIN").length;
  const losses = items.filter((r) => r.status === "LOSS").length;
  const draws = items.filter((r) => r.status === "DRAW").length;
  const settled = wins + losses;
  const wr = settled ? Math.round((wins / settled) * 100) : 0;
  return { wins, losses, draws, wr };
};

const PAGE_SIZE = 2; // months per page (click More to reveal more)

const Archive = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("ALL");
  const [visibleMonths, setVisibleMonths] = useState(PAGE_SIZE);
  const [archiveStats, setArchiveStats] = useState(null);

  const load = async () => {
    try {
      const [list, stats] = await Promise.all([fetchMatches(), fetchStats()]);
      setRows(list);
      setArchiveStats(stats?.archive || null);
    } catch {}
  };

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("ep-data-updated", handler);
    const iv = setInterval(load, 20000);
    return () => {
      window.removeEventListener("ep-data-updated", handler);
      clearInterval(iv);
    };
  }, []);

  // reset visible-months when filters change
  useEffect(() => {
    setVisibleMonths(PAGE_SIZE);
  }, [query, status]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        r.home.toLowerCase().includes(q) ||
        r.away.toLowerCase().includes(q) ||
        r.league.toLowerCase().includes(q) ||
        r.tip.toLowerCase().includes(q);
      const matchesStatus = status === "ALL" || r.status === status;
      return matchesQuery && matchesStatus;
    });
  }, [rows, query, status]);

  const totals = useMemo(() => {
    if (archiveStats && archiveStats.mode === "manual") {
      return {
        total: archiveStats.totalPicks ?? 0,
        wins: archiveStats.wins ?? 0,
        losses: archiveStats.losses ?? 0,
        draws: 0,
        wr: archiveStats.winRate ?? 0,
      };
    }
    const wins = rows.filter((r) => r.status === "WIN").length;
    const losses = rows.filter((r) => r.status === "LOSS").length;
    const draws = rows.filter((r) => r.status === "DRAW").length;
    const settled = wins + losses;
    const wr = settled ? Math.round((wins / settled) * 100) : 0;
    return { wins, losses, draws, total: rows.length, wr };
  }, [rows, archiveStats]);

  const allGroups = useMemo(() => groupByMonth(filtered), [filtered]);
  const shownGroups = allGroups.slice(0, visibleMonths);
  const hasMore = allGroups.length > visibleMonths;

  return (
    <div className="bg-[#f5f7ff] min-h-screen">
      {/* Header */}
      <div className="bg-ep-royal py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="text-[#ff5a5a] font-semibold tracking-widest text-xs uppercase">
            Predictions Archive
          </div>
          <h1 className="text-white text-3xl sm:text-5xl font-bold mt-3">
            Full transparency — every past pick
          </h1>
          <p className="mt-4 text-white/70 max-w-2xl">
            Complete record of published picks, month by month. No result is
            ever removed — wins, losses, and draws all remain visible so you
            can independently verify our long-term performance.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Total picks" value={totals.total} />
            <MiniStat label="Wins" value={totals.wins} accent="text-emerald-400" />
            <MiniStat label="Losses" value={totals.losses} accent="text-[#ff9a9a]" />
            <MiniStat label="Win rate" value={`${totals.wr}%`} accent="text-[#ff5a5a]" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-8">
        <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-slate-200 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Search team, league or pick..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          <div className="w-full md:w-56">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-11">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All results</SelectItem>
                <SelectItem value="WIN">Wins only</SelectItem>
                <SelectItem value="LOSS">Losses only</SelectItem>
                <SelectItem value="DRAW">Draws only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Groups */}
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 space-y-10">
        {shownGroups.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center text-slate-500">
            No picks match your filters.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center gap-4 pt-2">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#ff5a5a]/60" />
              <h2 className="text-[#0a1a4a] text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[#ff5a5a]" />
                Past Months
                <span className="w-2 h-2 rounded-full bg-[#ff5a5a]" />
              </h2>
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#ff5a5a]/60" />
            </div>

            {shownGroups.map((g) => (
              <MonthGroup key={g.key} group={g} />
            ))}

            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button
                  onClick={() => setVisibleMonths((v) => v + PAGE_SIZE)}
                  className="bg-ep-coral hover:bg-[#ff7676] text-white h-12 px-8 rounded-lg font-semibold coral-glow group"
                >
                  Load more months
                  <ChevronDown className="ml-2 transition-transform group-hover:translate-y-0.5" size={18} />
                </Button>
              </div>
            )}

            {!hasMore && allGroups.length > PAGE_SIZE && (
              <div className="flex justify-center pt-2">
                <Button
                  onClick={() => navigate("/contact")}
                  className="bg-ep-coral hover:bg-[#ff7676] text-white h-12 px-8 rounded-lg font-semibold coral-glow tracking-wide"
                >
                  UNLOCK FULL ARCHIVE
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const MonthGroup = ({ group }) => {
  const t = monthTotals(group.items);
  return (
    <section className="fade-up">
      <div className="flex items-center gap-4 mb-4">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#0a1a4a]/25" />
        <div className="flex items-center gap-3 bg-[#0a1a4a] text-white px-5 py-2.5 rounded-full shadow-md">
          <Calendar size={16} className="text-[#ff5a5a]" />
          <span className="font-bold tracking-wide">{group.label}</span>
          <span className="hidden sm:inline text-white/50 text-xs">
            {group.items.length} pick{group.items.length !== 1 ? "s" : ""} · {t.wins}W {t.losses}L{t.draws ? ` ${t.draws}D` : ""} · {t.wr}%
          </span>
        </div>
        <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#0a1a4a]/25" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden divide-y divide-slate-100">
        {group.items.map((m) => (
          <MatchRow key={m.id} m={m} />
        ))}
      </div>
    </section>
  );
};

export const MatchRow = ({ m }) => {
  const s = statusStyles[m.status] || statusStyles.DRAW;
  const Icon = s.icon;

  return (
    <div className="group px-4 sm:px-6 py-4 hover:bg-slate-50 transition-colors">
      <div className="grid grid-cols-12 gap-3 items-center">
        <div className="col-span-4 sm:col-span-2">
          <div className="text-xs text-[#0a1a4a]/50 font-semibold uppercase tracking-wider">Date</div>
          <div className="text-sm font-semibold text-[#0a1a4a] whitespace-nowrap">{formatDate(m.date)}</div>
        </div>

        <div className="col-span-8 sm:col-span-5 lg:col-span-5">
          <div className="text-xs text-[#0a1a4a]/50 font-semibold uppercase tracking-wider">Match</div>
          <div className="text-sm font-bold text-[#0a1a4a] leading-snug">
            <div>{m.home}</div>
            <div className="text-[#0a1a4a]/40 text-xs font-medium py-0.5">—</div>
            <div>{m.away}</div>
          </div>
        </div>

        <div className="col-span-8 sm:col-span-5 lg:col-span-3">
          <div className="text-xs text-[#0a1a4a]/50 font-semibold uppercase tracking-wider">League</div>
          <div className="text-sm text-[#0a1a4a]/80 font-medium">{m.league}</div>
        </div>

        <div className="col-span-4 sm:col-span-2 lg:col-span-2 text-right">
          <div className="text-xs text-[#0a1a4a]/50 font-semibold uppercase tracking-wider">Odds</div>
          <div className="text-sm font-bold text-[#0a1a4a]">
            {m.odds && m.odds > 0 ? Number(m.odds).toFixed(2) : "—"}
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex items-center flex-wrap gap-3">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-extrabold tracking-wider ${s.pill}`}>
          <Icon size={13} strokeWidth={2.5} />
          {m.status}
        </span>
        <div className="text-sm text-[#0a1a4a]/70">
          <span className="font-semibold text-[#0a1a4a]/60">Tip for game: </span>
          <span className="font-bold text-[#0a1a4a]">{m.tip}</span>
        </div>
        {m.ftResult && m.ftResult !== "-" && (
          <div className="ml-auto text-xs text-[#0a1a4a]/60">
            <span className="font-semibold">FT: </span>
            <span className="font-bold text-[#0a1a4a]">{m.ftResult}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const MiniStat = ({ label, value, accent = "text-white" }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
    <div className="text-white/60 text-xs uppercase tracking-wider font-semibold">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${accent}`}>{value}</div>
  </div>
);

export default Archive;
